import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api.js';
import { useToast } from '../components/Toast.js';
import { PageHeader } from '../components/ui/index.js';

interface HealthData {
  worker: { healthy: boolean; lastSeen: string | null; metadata: { pid: number; uptime: number } | null };
  queue: { stats: Record<string, number>; deadLetters: number };
  notifications: { last24h: Record<string, number>; failed: number };
}

interface AgentHealthData {
  agent: { status: 'up' | 'down' | 'unknown'; url: string | null; lastChecked: string; responseTimeMs: number | null; error: string | null };
  deliveries: { last24h: Record<string, { delivered: number; failed: number; pending: number; lastFailureCode: number | null }> };
  destinations: { required: string[]; totalWebsiteOrgs: number; status: { eventType: string; configured: number; missing: number }[] };
  overall: 'healthy' | 'degraded' | 'down';
}

interface DeadLetterEvent {
  id: string;
  orgId: string;
  eventType: string;
  status: string;
  retryCount: number;
  createdAt: string;
  processedAt: string | null;
}

interface ActivityEntry {
  id: string;
  org_id: string | null;
  user_id: string | null;
  user_email: string | null;
  action: string;
  category: string;
  description: string | null;
  entity_type: string | null;
  entity_id: string | null;
  entity_name: string | null;
  changes: Record<string, unknown> | null;
  ip_address: string | null;
  created_at: string;
  source: string;
  org_name: string | null;
}

interface SiteJob {
  id: string;
  org_id: string;
  job_type: string;
  status: string;
  target_domain: string | null;
  error_message: string | null;
  error_stage: string | null;
  progress: Record<string, unknown> | null;
  claimed_by: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

type SystemTab = 'health' | 'dead-letters' | 'activity' | 'site-jobs';

export function SystemPage() {
  const [tab, setTab] = useState<SystemTab>('health');
  const { toast } = useToast();

  return (
    <div className="space-y-5">
      <PageHeader title="System" />

      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {([
          { key: 'health' as const, label: 'Health' },
          { key: 'dead-letters' as const, label: 'Dead Letters' },
          { key: 'activity' as const, label: 'Activity Log' },
          { key: 'site-jobs' as const, label: 'Site Jobs' },
        ]).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex-1 py-2 text-xs font-medium rounded-md transition-colors ${
              tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>{t.label}</button>
        ))}
      </div>

      {tab === 'health' && <HealthTab />}
      {tab === 'dead-letters' && <DeadLettersTab toast={toast} />}
      {tab === 'activity' && <ActivityLogTab />}
      {tab === 'site-jobs' && <SiteJobsTab />}
    </div>
  );
}

function HealthTab() {
  const [data, setData] = useState<HealthData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      const d = await apiFetch<HealthData>('/admin/health');
      setData(d);
    } catch { /* */ }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetch(); const timer = setInterval(fetch, 30_000); return () => clearInterval(timer); }, [fetch]);

  if (isLoading) return <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />)}</div>;
  if (!data) return <p className="text-sm text-red-600">Failed to load health data</p>;

  const workerAge = data.worker.lastSeen ? Math.round((Date.now() - new Date(data.worker.lastSeen).getTime()) / 1000) : null;

  return (
    <div className="space-y-4">
      {/* Worker Status */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${data.worker.healthy ? 'bg-green-500' : 'bg-red-500'}`} />
            <div>
              <p className="font-medium text-gray-900 text-sm">Queue Worker</p>
              <p className="text-xs text-gray-500">
                {data.worker.lastSeen
                  ? `Last seen ${workerAge}s ago${data.worker.metadata ? ` (uptime: ${Math.round(data.worker.metadata.uptime / 60)}min)` : ''}`
                  : 'Never seen'}
              </p>
            </div>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${data.worker.healthy ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {data.worker.healthy ? 'Healthy' : 'Unhealthy'}
          </span>
        </div>
      </div>

      {/* Event Queue */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <p className="font-medium text-gray-900 text-sm mb-3">Event Queue</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.entries(data.queue.stats).map(([status, count]) => (
            <div key={status} className="text-center">
              <p className={`text-2xl font-bold ${status === 'dead_letter' && count > 0 ? 'text-red-600' : status === 'failed' && count > 0 ? 'text-yellow-600' : 'text-gray-900'}`}>{count}</p>
              <p className="text-xs text-gray-500">{status.replace(/_/g, ' ')}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Notifications (24h) */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <p className="font-medium text-gray-900 text-sm mb-3">Notifications (last 24h)</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.entries(data.notifications.last24h).map(([status, count]) => (
            <div key={status} className="text-center">
              <p className={`text-2xl font-bold ${status === 'failed' && count > 0 ? 'text-red-600' : 'text-gray-900'}`}>{count}</p>
              <p className="text-xs text-gray-500">{status}</p>
            </div>
          ))}
        </div>
      </div>

      <AgentPipelineCard />
    </div>
  );
}

function AgentPipelineCard() {
  const [data, setData] = useState<AgentHealthData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchHealth = useCallback(async () => {
    try {
      const d = await apiFetch<AgentHealthData>('/admin/agent-health');
      setData(d);
    } catch { /* */ }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchHealth(); const timer = setInterval(fetchHealth, 30_000); return () => clearInterval(timer); }, [fetchHealth]);

  if (isLoading) return <div className="h-32 bg-gray-100 rounded-xl animate-pulse" />;
  if (!data) return null;

  const statusColor = data.overall === 'healthy' ? 'bg-green-500' : data.overall === 'degraded' ? 'bg-yellow-500' : 'bg-red-500';
  const statusBadgeColor = data.overall === 'healthy' ? 'bg-green-100 text-green-700' : data.overall === 'degraded' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700';

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${statusColor}`} />
          <div>
            <p className="font-medium text-gray-900 text-sm">Agent Pipeline</p>
            <p className="text-xs text-gray-500">
              {data.agent.status === 'up' && data.agent.responseTimeMs != null
                ? `Agent responding (${data.agent.responseTimeMs}ms)`
                : data.agent.status === 'down'
                  ? `Agent unreachable${data.agent.error ? `: ${data.agent.error}` : ''}`
                  : 'No agent URL configured'}
            </p>
          </div>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusBadgeColor}`}>
          {data.overall === 'healthy' ? 'Healthy' : data.overall === 'degraded' ? 'Degraded' : 'Down'}
        </span>
      </div>

      {/* Delivery Stats */}
      {Object.keys(data.deliveries.last24h).length > 0 && (
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Deliveries (24h)</p>
          <div className="space-y-1.5">
            {Object.entries(data.deliveries.last24h).map(([eventType, stats]) => {
              const total = stats.delivered + stats.failed + stats.pending;
              const successPct = total > 0 ? Math.round((stats.delivered / total) * 100) : 0;
              return (
                <div key={eventType} className="flex items-center gap-3">
                  <span className="text-xs font-mono text-gray-600 w-40 truncate">{eventType}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div className="bg-green-500 h-2 rounded-full" style={{ width: `${successPct}%` }} />
                  </div>
                  <span className="text-xs text-gray-500 w-28 text-right">
                    {stats.delivered}
                    {stats.failed > 0 && <span className="text-red-500 ml-1">/ {stats.failed} fail</span>}
                    {stats.pending > 0 && <span className="text-yellow-500 ml-1">/ {stats.pending} pending</span>}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {Object.keys(data.deliveries.last24h).length === 0 && (
        <p className="text-xs text-gray-400 italic">No deliveries in last 24h</p>
      )}

      {/* Destination Config Checklist */}
      <div>
        <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">
          Required Destinations ({data.destinations.totalWebsiteOrgs} website org{data.destinations.totalWebsiteOrgs !== 1 ? 's' : ''})
        </p>
        <div className="space-y-1">
          {data.destinations.status.map(dest => (
            <div key={dest.eventType} className="flex items-center gap-2 text-xs">
              {dest.missing === 0 ? (
                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              <span className="font-mono text-gray-700">{dest.eventType}</span>
              <span className="text-gray-400">
                {dest.configured} configured{dest.missing > 0 && <span className="text-red-500 font-medium">, {dest.missing} missing</span>}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DeadLettersTab({ toast }: { toast: (type: 'success' | 'error' | 'info', msg: string) => void }) {
  const [events, setEvents] = useState<DeadLetterEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    try {
      const d = await apiFetch<{ events: DeadLetterEvent[] }>('/admin/events?status=dead_letter&limit=50');
      setEvents(d.events);
    } catch { /* */ }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleRequeue = async (id: string) => {
    try {
      await apiFetch(`/admin/events/${id}/requeue`, { method: 'POST' });
      toast('success', 'Event re-queued');
      fetch();
    } catch { toast('error', 'Failed to re-queue'); }
  };

  if (isLoading) return <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 bg-gray-100 rounded-xl animate-pulse" />)}</div>;

  if (events.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-sm text-gray-900 font-medium">No dead letters</p>
        <p className="text-xs text-gray-500 mt-1">All events are processing normally.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {events.map(evt => (
        <div key={evt.id} className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-mono">{evt.eventType}</span>
                <span className="text-xs text-gray-400">retries: {evt.retryCount}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Created {new Date(evt.createdAt).toLocaleString()} — ID: {evt.id.slice(0, 8)}...
              </p>
            </div>
            <button onClick={() => handleRequeue(evt.id)}
              className="text-xs bg-primary-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-primary-700">
              Re-queue
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

const JOB_STATUS_COLORS: Record<string, string> = {
  queued: 'bg-blue-100 text-blue-700',
  converting: 'bg-yellow-100 text-yellow-700',
  building: 'bg-yellow-100 text-yellow-700',
  deploying: 'bg-purple-100 text-purple-700',
  live: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
  pending: 'bg-gray-100 text-gray-600',
  validating: 'bg-blue-100 text-blue-600',
};

function SiteJobsTab() {
  const [jobs, setJobs] = useState<SiteJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const fetchJobs = useCallback(async () => {
    setIsLoading(true);
    try {
      const d = await apiFetch<{ jobs: SiteJob[]; total: number }>('/site-jobs?limit=50');
      setJobs(d.jobs);
      setTotal(d.total);
    } catch { /* */ }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchJobs(); const timer = setInterval(fetchJobs, 15_000); return () => clearInterval(timer); }, [fetchJobs]);

  if (isLoading) return <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}</div>;

  if (jobs.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-gray-500">No site jobs yet.</p>
        <p className="text-xs text-gray-400 mt-1">Jobs are created when sites are provisioned, rebuilt, or migrated.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-500">{total} total jobs</p>
      <div className="space-y-2">
        {jobs.map(job => (
          <div key={job.id} className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${JOB_STATUS_COLORS[job.status] ?? 'bg-gray-100 text-gray-600'}`}>
                    {job.status}
                  </span>
                  <span className="text-xs font-mono text-gray-400">{job.job_type}</span>
                </div>
                <p className="text-sm text-gray-900 mt-1 truncate">{job.target_domain ?? job.id.slice(0, 8)}</p>
                {job.error_message && (
                  <p className="text-xs text-red-500 mt-0.5 truncate">{job.error_stage ? `[${job.error_stage}] ` : ''}{job.error_message}</p>
                )}
                {job.progress && (job.progress as Record<string, string>).step && (
                  <p className="text-xs text-gray-400 mt-0.5">{String((job.progress as Record<string, string>).step)}: {String((job.progress as Record<string, string>).detail ?? '')}</p>
                )}
              </div>
              <div className="text-right text-xs text-gray-400 flex-shrink-0 ml-3">
                <p>{new Date(job.created_at).toLocaleString()}</p>
                {job.claimed_by && <p className="font-mono">{job.claimed_by}</p>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const CATEGORIES = ['auth', 'entity', 'user_mgmt', 'integration', 'config', 'compliance', 'system'] as const;
const CATEGORY_COLORS: Record<string, string> = {
  auth: 'bg-red-500', entity: 'bg-blue-500', user_mgmt: 'bg-purple-500',
  integration: 'bg-orange-500', config: 'bg-gray-500', compliance: 'bg-yellow-500', system: 'bg-green-500',
};
const DATE_RANGES = [
  { key: '1h', label: '1h', ms: 3600_000 },
  { key: '24h', label: '24h', ms: 86400_000 },
  { key: '7d', label: '7d', ms: 604800_000 },
  { key: '30d', label: '30d', ms: 2592000_000 },
  { key: 'all', label: 'All', ms: 0 },
];

function ActivityLogTab() {
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState('24h');
  const [categoryFilter, setCategoryFilter] = useState<Set<string>>(new Set(CATEGORIES));
  const [orgSearch, setOrgSearch] = useState('');
  const [actionSearch, setActionSearch] = useState('');
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchEntries = useCallback(async (cursor?: string) => {
    if (!cursor) setIsLoading(true);
    try {
      const params = new URLSearchParams();
      const cats = Array.from(categoryFilter);
      if (cats.length < CATEGORIES.length) params.set('category', cats.join(','));
      if (actionSearch) params.set('action', actionSearch);
      const range = DATE_RANGES.find(r => r.key === dateRange);
      if (range && range.ms > 0) params.set('from', new Date(Date.now() - range.ms).toISOString());
      if (cursor) params.set('cursor', cursor);
      params.set('limit', '100');

      const d = await apiFetch<{ entries: ActivityEntry[]; next_cursor: string | null }>(`/admin/activity?${params}`);
      if (cursor) {
        setEntries(prev => [...prev, ...d.entries]);
      } else {
        setEntries(d.entries);
      }
      setNextCursor(d.next_cursor);
    } catch { /* */ }
    finally { setIsLoading(false); }
  }, [categoryFilter, dateRange, actionSearch]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const toggleCategory = (cat: string) => {
    setCategoryFilter(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  // Filter entries client-side for org search (server resolves org_name)
  const filtered = orgSearch
    ? entries.filter(e => e.org_name?.toLowerCase().includes(orgSearch.toLowerCase()))
    : entries;

  return (
    <div className="space-y-3">
      {/* Date range pills */}
      <div className="flex items-center gap-2 flex-wrap">
        {DATE_RANGES.map(r => (
          <button key={r.key} onClick={() => setDateRange(r.key)}
            className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
              dateRange === r.key ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500 hover:text-gray-700'
            }`}>{r.label}</button>
        ))}
      </div>

      {/* Category pills */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {CATEGORIES.map(cat => (
          <button key={cat} onClick={() => toggleCategory(cat)}
            className={`px-2.5 py-1 text-xs rounded-full font-medium transition-colors flex items-center gap-1.5 ${
              categoryFilter.has(cat) ? 'bg-gray-200 text-gray-800' : 'bg-gray-50 text-gray-400'
            }`}>
            <span className={`w-2 h-2 rounded-full ${CATEGORY_COLORS[cat] ?? 'bg-gray-400'}`} />
            {cat.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      {/* Search filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input value={orgSearch} onChange={e => setOrgSearch(e.target.value)}
          placeholder="Filter by org name..."
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500" />
        <input value={actionSearch} onChange={e => setActionSearch(e.target.value)}
          placeholder="Filter by action..."
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-1">{[1,2,3,4,5].map(i => <div key={i} className="h-10 bg-gray-100 rounded-lg animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <p className="text-center py-8 text-sm text-gray-500">No activity entries found.</p>
      ) : (
        <div className="space-y-0.5">
          {/* Header */}
          <div className="grid grid-cols-10 gap-2 px-3 py-1.5 text-xs font-medium text-gray-400 uppercase tracking-wide">
            <div className="col-span-1"></div>
            <div className="col-span-3">Action</div>
            <div className="col-span-2">User</div>
            <div className="col-span-2">Org</div>
            <div className="col-span-2 text-right">Time</div>
          </div>

          {filtered.map(entry => (
            <div key={entry.id}>
              <button onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                className="w-full grid grid-cols-10 gap-2 px-3 py-2 text-xs hover:bg-gray-50 rounded-lg transition-colors items-center text-left">
                <div className="col-span-1">
                  <span className={`w-2.5 h-2.5 rounded-full inline-block ${CATEGORY_COLORS[entry.category] ?? 'bg-gray-400'}`} />
                </div>
                <div className="col-span-3 font-medium text-gray-900 truncate">
                  {entry.action.replace(/\./g, ' ').replace(/_/g, ' ')}
                  {entry.source === 'audit' && <span className="ml-1 text-red-500">*</span>}
                  {entry.ip_address && (
                    <span className="ml-1.5 text-gray-300 cursor-help" title={entry.ip_address}>
                      <svg className="w-3 h-3 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" /></svg>
                    </span>
                  )}
                </div>
                <div className="col-span-2 text-gray-600 truncate">{entry.user_email ?? '—'}</div>
                <div className="col-span-2 text-gray-500 truncate">{entry.org_name ?? (entry.user_email ? 'CRM98' : '—')}</div>
                <div className="col-span-2 text-gray-400 text-right whitespace-nowrap">{new Date(entry.created_at).toLocaleString()}</div>
              </button>

              {expandedId === entry.id && (
                <div className="mx-3 mb-2 p-3 bg-gray-50 rounded-lg text-xs space-y-2">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    <div><span className="text-gray-400">Category:</span> <span className="text-gray-700">{entry.category}</span></div>
                    <div><span className="text-gray-400">Source:</span> <span className="text-gray-700">{entry.source}</span></div>
                    <div><span className="text-gray-400">Full IP:</span> <span className="text-gray-700 font-mono">{entry.ip_address ?? '—'}</span></div>
                    <div><span className="text-gray-400">Org ID:</span> <span className="text-gray-700 font-mono">{entry.org_id?.slice(0, 8) ?? '—'}</span></div>
                    {entry.description && <div className="col-span-2"><span className="text-gray-400">Description:</span> <span className="text-gray-700">{entry.description}</span></div>}
                  </div>
                  {entry.changes && Object.keys(entry.changes).length > 0 && (
                    <div>
                      <p className="text-gray-400 mb-1">Changes / Metadata:</p>
                      <pre className="bg-white border border-gray-200 rounded p-2 overflow-auto max-h-40 text-gray-700 font-mono">
                        {JSON.stringify(entry.changes, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {nextCursor && (
            <button onClick={() => fetchEntries(nextCursor)}
              className="w-full py-2 text-sm text-primary-600 hover:text-primary-800 font-medium">
              Load more
            </button>
          )}
        </div>
      )}
    </div>
  );
}
